import { useState, useRef, useEffect } from 'react';
import {
  CheckCircle2, X, ChevronDown, Check, Search
} from 'lucide-react';
import { STATUS_CFG } from '../../../helpers/Project-Report/utils/statusConfig';
import { avatarColors } from '../../../helpers/Project-Report/utils/helpers';
import { Tooltip } from 'primereact/tooltip';
import SubCategoryDropdown from './SubCategoryDropdown';

const BoardTab = ({ tasks, setTasks, accent, picOptions, picSearch, setPicSearch, onPicSearch, onRefreshPic, permissions }) => {
  const [filterPIC, setFilterPIC] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  // Custom Dropdown states for Modal
  const [activeDropdown, setActiveDropdown] = useState(null); // 'pic' | 'status' | null
  const dropdownRef = useRef(null);

  // const isReadOnly = (isEdited === 'false' || isEdited === false) && (onIframe === 'true' || onIframe === true);
  const isReadOnly = !permissions.includes("project_report:edit");

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Extract unique PICs from tasks that have a PIC assigned
  const uniquePICs = Array.from(new Set(tasks.map(t => t.pic).filter(Boolean)));

  const handleDragStart = (e, taskId) => {
    e.dataTransfer.effectAllowed = 'move';
    // Let the browser paint the full-opacity drag ghost before dimming the original
    setTimeout(() => {
      setDraggedTask(taskId);
    }, 0);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, statusKey) => {
    e.preventDefault();
    if (draggedTask) {
      setTasks(prev => prev.map(t => t.id === draggedTask ? { ...t, status: statusKey } : t));
      setDraggedTask(null);
    }
  };

  const updateSelectedTask = (updates) => {
    setSelectedTask(prev => ({ ...prev, ...updates }));
    setTasks(prev => prev.map(t => t.id === selectedTask.id ? { ...t, ...updates } : t));
  };

  const filteredTasks = filterPIC ? tasks.filter(t => t.pic === filterPIC) : tasks;

  // Small helper for PIC avatar
  const getAvatarInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (name) => {
    if (!name) return '#cbd5e1';
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    const h = hash % 360;
    return `hsl(${h}, 70%, 50%)`;
  };

  return (
    <div>
      {/* Action Bar (Filters) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Filter by assignee:</span>
          <div style={{ display: 'flex', gap: '-6px' }}>
            {uniquePICs.map((pic, i) => (
              <div
                key={pic}
                onClick={() => setFilterPIC(filterPIC === pic ? null : pic)}
                title={pic}
                style={{
                  width: '32px', height: '32px', borderRadius: '50%',
                  background: getAvatarColor(pic),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '11px', fontWeight: '700', color: '#fff',
                  border: filterPIC === pic ? `2px solid ${accent}` : '2px solid #fff',
                  marginLeft: i > 0 ? '-8px' : 0,
                  boxShadow: filterPIC === pic ? '0 0 0 2px #fff inset, 0 2px 8px rgba(0,0,0,0.15)' : '0 2px 6px rgba(0,0,0,0.12)',
                  zIndex: filterPIC === pic ? 100 : uniquePICs.length - i,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  transform: filterPIC === pic ? 'translateY(-2px) scale(1.1)' : 'none',
                  opacity: filterPIC && filterPIC !== pic ? 0.5 : 1
                }}
              >
                {getAvatarInitials(pic)}
              </div>
            ))}
          </div>
        </div>

        {filterPIC && (
          <button
            onClick={() => setFilterPIC(null)}
            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: '12px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Kanban Board Layout */}
      <div style={{ display: 'flex', gap: '16px', overflowX: 'auto', paddingBottom: '16px', minHeight: '500px' }}>
        {Object.entries(STATUS_CFG).map(([statusKey, st]) => {
          const colTasks = filteredTasks.filter(t => t.status === statusKey);

          return (
            <div
              key={statusKey}
              style={{ flex: '0 0 280px', background: '#f8fafc', borderRadius: '12px', padding: '12px', display: 'flex', flexDirection: 'column', border: '1px solid #f1f5f9' }}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, statusKey)}
            >
              {/* Column Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', padding: '0 4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: st.color }} />
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{st.label}</span>
                </div>
                <span style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8', background: '#e2e8f0', padding: '2px 8px', borderRadius: '12px' }}>{colTasks.length} OF {tasks.filter(t => t.status === statusKey).length}</span>
              </div>

              {/* Task Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                {colTasks.map(task => (
                  <div
                    key={task.id}
                    draggable={!isReadOnly}
                    onDragStart={(e) => { if (!isReadOnly) handleDragStart(e, task.id); }}
                    onClick={() => setSelectedTask(task)}
                    style={{
                      background: '#fff', padding: '14px', borderRadius: '10px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0',
                      outline: '2px solid transparent', outlineOffset: '2px', // Persiapan outline tebal yang tidak menggeser layout
                      cursor: isReadOnly ? 'pointer' : (draggedTask === task.id ? 'move' : 'pointer'), // Memperbaiki bug kursor tidak terlihat di beberapa OS
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'flex', flexDirection: 'column', gap: '12px',
                      opacity: draggedTask === task.id ? 0.3 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (draggedTask !== task.id) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)';
                        e.currentTarget.style.outline = `2px solid ${accent}`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                      e.currentTarget.style.outline = '2px solid transparent';
                    }}
                  >
                    <div style={{ fontSize: '13.5px', fontWeight: '600', color: '#0f172a', lineHeight: 1.4 }}>
                      {task.name}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <CheckCircle2 size={13} color={st.color} />
                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>TASK-{task.id}</span>
                      </div>

                      {task.pic && (
                        <div
                          title={task.pic}
                          style={{ width: '24px', height: '24px', borderRadius: '50%', background: getAvatarColor(task.pic), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: '#fff' }}
                        >
                          {getAvatarInitials(task.pic)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <>
          <div onClick={() => setSelectedTask(null)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', animation: 'fadeIn 0.2s ease' }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1001, background: '#fff', borderRadius: '16px', width: '90%', maxWidth: '850px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 48px rgba(0,0,0,0.2)', animation: 'fadeInScale 0.25s cubic-bezier(0.16, 1, 0.3, 1)', overflow: 'hidden' }}>

            {/* Modal Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '13px', fontWeight: '500' }}>
                  <CheckCircle2 size={16} color={accent} />
                  <span>TASK-{selectedTask.id}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setSelectedTask(null)} style={{ width: '32px', height: '32px', borderRadius: '8px', border: 'none', background: '#f1f5f9', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

              {/* Left Pane (Main Content) */}
              <div style={{ flex: 1, padding: '24px', overflowY: 'auto', borderRight: '1px solid #f1f5f9' }}>
                <input
                  type="text"
                  value={selectedTask.name}
                  readOnly={isReadOnly}
                  onChange={(e) => !isReadOnly && updateSelectedTask({ name: e.target.value })}
                  style={{ width: '100%', fontSize: '24px', fontWeight: '700', color: '#0f172a', border: '1px solid transparent', outline: 'none', padding: '8px 12px', marginLeft: '-12px', borderRadius: '8px', transition: 'all 0.2s', fontFamily: "'Inter', sans-serif", marginBottom: '24px', cursor: isReadOnly ? 'default' : 'text' }}
                  onFocus={(e) => { if (!isReadOnly) { e.target.style.background = '#f8fafc'; e.target.style.borderColor = '#e2e8f0'; } }}
                  onBlur={(e) => { if (!isReadOnly) { e.target.style.background = 'transparent'; e.target.style.borderColor = 'transparent'; } }}
                />

                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', marginBottom: '12px' }}>Description</h4>
                  <textarea
                    placeholder="Add a description..."
                    value={selectedTask.description || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => !isReadOnly && updateSelectedTask({ description: e.target.value })}
                    style={{ width: '100%', minHeight: '120px', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fafafa', fontSize: '13.5px', color: '#334155', fontFamily: "'Inter', sans-serif", outline: 'none', resize: 'vertical', cursor: isReadOnly ? 'default' : 'text' }}
                    onFocus={(e) => { if (!isReadOnly) { e.target.style.borderColor = accent; } }}
                    onBlur={(e) => { if (!isReadOnly) { e.target.style.borderColor = '#e2e8f0'; } }}
                  />
                </div>
              </div>

              {/* Right Pane (Sidebar Details) */}
              <div style={{ width: '320px', padding: '24px', overflowY: 'auto', background: '#f8fafc', overflow: 'visible' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Details</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* PIC Dropdown */}
                  <div style={{ position: 'relative' }} ref={activeDropdown === 'pic' ? dropdownRef : null}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Assignee</label>
                    <Tooltip target=".pic-modal-selected-tooltip" position="top" mouseTrack mouseTrackTop={15} pt={{ text: { style: { fontSize: '11px', padding: '4px 8px', fontWeight: '500' } } }} />
                    <button
                      className="pic-modal-selected-tooltip"
                      data-pr-tooltip={selectedTask.pic ? String(selectedTask.pic).split('-').slice(1).join('-') || selectedTask.pic : 'Klik untuk mengubah Assignee'}
                      onClick={() => {
                        if (isReadOnly) return;
                        if (activeDropdown === 'pic') {
                          setActiveDropdown(null);
                        } else {
                          setActiveDropdown('pic');
                          setPicSearch('');
                          onRefreshPic();
                        }
                      }}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: activeDropdown === 'pic' ? `1.5px solid ${accent}` : '1.5px solid #cbd5e1', fontSize: '13px', color: '#0f172a', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', outline: 'none', boxShadow: activeDropdown === 'pic' ? `0 0 0 3px ${accent}20` : 'none' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {selectedTask.pic ? (
                          <>
                            <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: getAvatarColor(selectedTask.pic), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: '#fff' }}>
                              {getAvatarInitials(selectedTask.pic)}
                            </div>
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{String(selectedTask.pic).split('-').slice(1).join('-') || selectedTask.pic}</span>
                          </>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>Unassigned</span>
                        )}
                      </div>
                      <ChevronDown size={14} color="#64748b" style={{ transform: activeDropdown === 'pic' ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                    </button>

                    {activeDropdown === 'pic' && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '240px', display: 'flex', flexDirection: 'column', animation: 'fadeInScale 0.15s ease' }}>
                        <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Search size={14} color="#94a3b8" />
                          <input
                            autoFocus
                            value={picSearch}
                            onChange={(e) => {
                              const val = e.target.value;
                              setPicSearch(val);
                              onPicSearch(val);
                            }}
                            placeholder="Cari PIC..."
                            style={{ flex: 1, border: 'none', outline: 'none', fontSize: '12px', color: '#0f172a' }}
                          />
                        </div>
                        <div style={{ overflowY: 'auto', flex: 1, padding: '4px' }}>
                          <Tooltip target=".pic-modal-option-tooltip" position="right" mouseTrack mouseTrackTop={15} pt={{ text: { style: { fontSize: '11px', padding: '4px 8px', fontWeight: '500' } } }} />
                          <div
                            className="pic-modal-option-tooltip"
                            data-pr-tooltip="Kosongkan"
                            onClick={() => { updateSelectedTask({ pic: '' }); setActiveDropdown(null); }}
                            style={{ padding: '8px 12px', fontSize: '12.5px', color: '#64748b', cursor: 'pointer', borderRadius: '6px' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            — Kosongkan —
                          </div>
                          {picOptions.filter(p => !picSearch || (p.nama && p.nama.toLowerCase().includes(picSearch.toLowerCase()))).map((p, i) => {
                            const picValue = p.NIK ? `${p.NIK}-${p.nama}` : p.nama;
                            const isSelected = selectedTask.pic === picValue || selectedTask.pic === p.nama;
                            return (
                              <div
                                key={i}
                                className="pic-modal-option-tooltip"
                                data-pr-tooltip={p.nama || 'Kosongkan'}
                                onClick={() => { updateSelectedTask({ pic: picValue }); setActiveDropdown(null); }}
                                style={{ padding: '8px 12px', fontSize: '12.5px', color: '#0f172a', cursor: 'pointer', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', background: isSelected ? `${accent}15` : 'transparent', fontWeight: isSelected ? '600' : '400' }}
                                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                              >
                                <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: p.nama ? avatarColors[p.id % avatarColors.length] : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>
                                  {p.avatar || '?'}
                                </div>
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px', fontWeight: isSelected ? '700' : '600' }}>
                                    {p.nama || '— Kosongkan —'}
                                  </span>
                                  {p.nama && (
                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                      {p.NIK} - {p.department}
                                    </span>
                                  )}
                                </div>
                                {isSelected && <Check size={12} color={accent} style={{ flexShrink: 0 }} />}
                              </div>
                            );
                          })}
                          {picOptions.filter(p => !picSearch || (p.nama && p.nama.toLowerCase().includes(picSearch.toLowerCase()))).length === 0 && (
                            <div style={{ padding: '12px', textAlign: 'center', fontSize: '11.5px', color: '#94a3b8' }}>
                              Tidak ditemukan
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sub Category */}
                  {/* <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Sub Category</label>
                      <SubCategoryDropdown
                        taskId={`modal-${selectedTask.id}`}
                        value={selectedTask.subCategory}
                        onChange={(catId) => updateSelectedTask({ subCategory: catId })}
                        accent={accent}
                        isReadOnly={isReadOnly}
                        cellInputStyle={{
                          width: '100%', padding: '8px 12px', borderRadius: '8px',
                          border: '1.5px solid #cbd5e1', fontSize: '13px', color: '#0f172a',
                          background: '#fff', cursor: 'pointer', outline: 'none'
                        }}
                        onOpen={() => setActiveDropdown(null)}
                      />
                    </div> */}

                  {/* Status Dropdown */}
                  <div style={{ position: 'relative' }} ref={activeDropdown === 'status' ? dropdownRef : null}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Status</label>
                    <button
                      onClick={() => { if (!isReadOnly) setActiveDropdown(activeDropdown === 'status' ? null : 'status'); }}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: activeDropdown === 'status' ? `1.5px solid ${accent}` : '1.5px solid #cbd5e1', fontSize: '13px', color: '#0f172a', background: STATUS_CFG[selectedTask.status]?.bg || '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', outline: 'none', boxShadow: activeDropdown === 'status' ? `0 0 0 3px ${accent}20` : 'none' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: STATUS_CFG[selectedTask.status]?.color || '#cbd5e1' }} />
                        <span style={{ color: STATUS_CFG[selectedTask.status]?.color || '#0f172a', fontWeight: '600' }}>
                          {STATUS_CFG[selectedTask.status]?.label || 'Set Status'}
                        </span>
                      </div>
                      <ChevronDown size={14} color={STATUS_CFG[selectedTask.status]?.color || '#64748b'} style={{ transform: activeDropdown === 'status' ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                    </button>

                    {activeDropdown === 'status' && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 100, padding: '4px', display: 'flex', flexDirection: 'column', animation: 'fadeInScale 0.15s ease' }}>
                        {Object.entries(STATUS_CFG).map(([k, v]) => (
                          <div
                            key={k}
                            onClick={() => { updateSelectedTask({ status: k }); setActiveDropdown(null); }}
                            style={{ padding: '8px 12px', fontSize: '12.5px', color: v.color, fontWeight: '600', cursor: 'pointer', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', background: selectedTask.status === k ? v.bg : 'transparent' }}
                            onMouseEnter={(e) => { if (selectedTask.status !== k) e.currentTarget.style.background = '#f8fafc'; }}
                            onMouseLeave={(e) => { if (selectedTask.status !== k) e.currentTarget.style.background = 'transparent'; }}
                          >
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: v.color }} />
                            {v.label}
                            {selectedTask.status === k && <Check size={14} color={v.color} style={{ marginLeft: 'auto' }} />}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Schedule Date */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Schedule Date</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="date" disabled={isReadOnly} value={selectedTask.scheduleStart || ''} onChange={(e) => updateSelectedTask({ scheduleStart: e.target.value })} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12px', width: '0', cursor: isReadOnly ? 'default' : 'text' }} />
                      <span style={{ color: '#94a3b8' }}>-</span>
                      <input type="date" disabled={isReadOnly} min={selectedTask.scheduleStart || ''} value={selectedTask.scheduleEnd || ''} onChange={(e) => updateSelectedTask({ scheduleEnd: e.target.value })} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12px', width: '0', cursor: isReadOnly ? 'default' : 'text' }} />
                    </div>
                  </div>

                  {/* Actual Date */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Actual Date</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="date" disabled={isReadOnly} value={selectedTask.actualStart || ''} onChange={(e) => updateSelectedTask({ actualStart: e.target.value })} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12px', width: '0', cursor: isReadOnly ? 'default' : 'text' }} />
                      <span style={{ color: '#94a3b8' }}>-</span>
                      <input type="date" disabled={isReadOnly} min={selectedTask.actualStart || ''} value={selectedTask.actualEnd || ''} onChange={(e) => updateSelectedTask({ actualEnd: e.target.value })} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12px', width: '0', cursor: isReadOnly ? 'default' : 'text' }} />
                    </div>
                  </div>

                  {/* Progress */}
                  <div>
                    <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                      Progress <span>{selectedTask.progress}%</span>
                    </label>
                    <input
                      type="range"
                      min="0" max="100"
                      disabled={isReadOnly}
                      value={selectedTask.progress === undefined ? 0 : selectedTask.progress}
                      onChange={(e) => updateSelectedTask({ progress: parseInt(e.target.value) })}
                      style={{ width: '100%', accentColor: accent, cursor: isReadOnly ? 'default' : 'ew-resize' }}
                    />
                  </div>

                </div>
              </div>
            </div>

          </div>
        </>
      )}
    </div>
  );
};

export default BoardTab;
